import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  ArrowLeft, Pencil, CheckCircle, PowerOff, Loader2, Star,
  Zap, Phone, Mail, Hash, Shield, MapPin, Building2, User,
  Package, TrendingUp, Award, AlertTriangle, MessageSquare,
  BarChart3, Activity, ClipboardList, RefreshCw, ExternalLink,
  ShieldCheck, ShieldAlert, ShieldX, XCircle, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { CarrierForm, BLANK_FORM, StatusPill, RatingBar } from './CarriersPage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_STYLE = {
  BRONZE:   { bg: 'bg-amber-500/10',  text: 'text-amber-600 dark:text-amber-400'  },
  SILVER:   { bg: 'bg-slate-400/10',  text: 'text-slate-500 dark:text-slate-300'  },
  GOLD:     { bg: 'bg-yellow-400/10', text: 'text-yellow-600 dark:text-yellow-400' },
  PLATINUM: { bg: 'bg-blue-500/10',   text: 'text-blue-500  dark:text-blue-400'   },
};

function scoreColor(v) {
  if (v >= 80) return 'text-emerald-500';
  if (v >= 60) return 'text-blue-500';
  if (v >= 40) return 'text-amber-500';
  return 'text-red-500';
}

function ScoreBar({ label, value, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground capitalize">{label.replace(/_/g, ' ')}</span>
        <span className={`font-semibold ${scoreColor(value)}`}>{value.toFixed(1)}</span>
      </div>
      <div className="h-1.5 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value, mono = false }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className={`text-sm text-right font-medium text-foreground ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color = 'text-foreground' }) {
  return (
    <div className="bg-muted/40 rounded-xl p-4 flex items-start gap-3">
      <div className="p-2 rounded-lg bg-muted">
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-xl font-bold ${color} tabular-nums`}>{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

export default function CarrierProfilePage() {
  const { carrierId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [carrier, setCarrier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [ezData, setEzData] = useState(null);
  const [cdRatings, setCdRatings] = useState(null);
  const [cdLoading, setCdLoading] = useState(false);
  const [fmcsaChecks, setFmcsaChecks] = useState(null);
  const [fmcsaLoading, setFmcsaLoading] = useState(false);

  const fetchCarrier = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Not found');
      setCarrier(await res.json());
    } catch {
      toast.error('Carrier not found');
      navigate('/admin/carriers');
    } finally {
      setLoading(false);
    }
  }, [carrierId, token, navigate]);

  useEffect(() => { fetchCarrier(); }, [fetchCarrier]);

  useEffect(() => {
    if (!token) return;
    const fetchEz = async () => {
      try {
        const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/ez-score`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setEzData(await res.json());
      } catch { /* silent */ }
    };
    fetchEz();
  }, [carrierId, token]);

  const fetchCdRatings = useCallback(async () => {
    if (!token) return;
    setCdLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/cd-ratings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCdRatings(await res.json());
    } catch { /* silent */ }
    finally { setCdLoading(false); }
  }, [carrierId, token]);

  useEffect(() => { fetchCdRatings(); }, [fetchCdRatings]);

  const fetchFmcsaChecks = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/fmcsa-checks`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setFmcsaChecks(await res.json());
    } catch { /* silent */ }
  }, [carrierId, token]);

  useEffect(() => { fetchFmcsaChecks(); }, [fetchFmcsaChecks]);

  const handleRunFmcsaCheck = async () => {
    setFmcsaLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/fmcsa-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Check failed');
      }
      toast.success('FMCSA check completed');
      fetchFmcsaChecks();
    } catch (err) { toast.error(err.message); }
    finally { setFmcsaLoading(false); }
  };

  const openEdit = () => {
    setForm({
      company_name: carrier.company_name || '',
      contact_name: carrier.contact_name || '',
      phone: carrier.phone || '',
      email: carrier.email || '',
      mc_number: carrier.mc_number || '',
      dot_number: carrier.dot_number || '',
      customer_id: carrier.customer_id || '',
      is_active: carrier.is_active !== false,
      equipment_types: carrier.equipment_types || [],
      soft_straps_supported: carrier.soft_straps_supported || false,
      regions_served: carrier.regions_served || [],
      insurance_limit: carrier.insurance_limit || 0,
    });
    setEditOpen(true);
  };

  const handleUpdate = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      toast.success('Carrier updated');
      setEditOpen(false);
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleVerify = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/verify`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to verify');
      toast.success('Carrier verified');
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
  };

  const handleToggle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/carriers/${carrierId}/toggle-active`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to toggle');
      const data = await res.json();
      toast.success(`Carrier ${data.is_active ? 'activated' : 'deactivated'}`);
      fetchCarrier();
    } catch (err) { toast.error(err.message); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32 text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading carrier…
      </div>
    );
  }

  if (!carrier) return null;

  const isPending = carrier.is_active !== false && carrier.status !== 'active_verified';
  const onTimeRate = carrier.on_time_rate != null ? `${(carrier.on_time_rate * 100).toFixed(1)}%` : '—';
  const damageRate = carrier.damage_rate != null ? `${(carrier.damage_rate * 100).toFixed(1)}%` : '—';
  const cancelRate = carrier.cancel_rate != null ? `${(carrier.cancel_rate * 100).toFixed(1)}%` : null;
  const claimRate = carrier.claim_rate != null ? `${(carrier.claim_rate * 100).toFixed(1)}%` : null;
  const commRating = carrier.communication_rating != null ? carrier.communication_rating.toFixed(1) : '—';

  return (
    <>
      <div className="space-y-6 max-w-5xl">

        {/* Top nav */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <button
            onClick={() => navigate('/admin/carriers')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Carriers
          </button>

          <div className="flex items-center gap-2">
            {isPending && (
              <Button
                onClick={handleVerify}
                className="bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20 border-0 h-9"
                variant="outline"
              >
                <CheckCircle className="h-4 w-4 mr-1.5" /> Verify
              </Button>
            )}
            <Button
              onClick={handleToggle}
              variant="outline"
              className="h-9"
            >
              <PowerOff className="h-4 w-4 mr-1.5" />
              {carrier.is_active !== false ? 'Deactivate' : 'Activate'}
            </Button>
            <Button
              onClick={openEdit}
              className="bg-accent text-white hover:bg-accent/90 h-9"
            >
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Button>
          </div>
        </div>

        {/* Profile header */}
        <div className="bg-card border border-border rounded-2xl p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <h1 className="font-outfit text-2xl font-bold text-foreground">{carrier.company_name}</h1>
              {carrier.contact_name && (
                <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
                  <User className="h-3.5 w-3.5" /> {carrier.contact_name}
                </p>
              )}
            </div>
            <StatusPill carrier={carrier} />
          </div>

          {/* Quick contact strip */}
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            {carrier.phone && (
              <a href={`tel:${carrier.phone}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Phone className="h-3.5 w-3.5" /> {carrier.phone}
              </a>
            )}
            {carrier.email && (
              <a href={`mailto:${carrier.email}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
                <Mail className="h-3.5 w-3.5" /> {carrier.email}
              </a>
            )}
            {carrier.mc_number && (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Hash className="h-3.5 w-3.5" /> MC# <span className="font-mono font-medium text-foreground">{carrier.mc_number}</span>
              </span>
            )}
            {carrier.dot_number && (
              <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Shield className="h-3.5 w-3.5" /> DOT# <span className="font-mono font-medium text-foreground">{carrier.dot_number}</span>
              </span>
            )}
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Rating" value={carrier.rating_internal ?? 50} sub="out of 100" icon={Star} color="text-amber-500" />
          <StatCard label="Deliveries" value={carrier.total_deliveries ?? 0} icon={Package} />
          <StatCard label="On-Time Rate" value={onTimeRate} icon={TrendingUp} color="text-emerald-500" />
          <StatCard label="Damage Rate" value={damageRate} icon={AlertTriangle} color={carrier.damage_rate > 0.05 ? 'text-red-500' : 'text-foreground'} />
        </div>

        {/* ── EZ Score highlighted box ── */}
        {ezData?.score && (() => {
          const s    = ezData.score;
          const tier = TIER_STYLE[s.tier] || TIER_STYLE.BRONZE;
          return (
            <div className="rounded-2xl border-2 border-accent/40 bg-accent/[0.03] p-5 space-y-5 shadow-sm">

              {/* Section header */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2.5">
                  <div className="p-1.5 rounded-lg bg-accent/10">
                    <BarChart3 className="h-4 w-4 text-accent" strokeWidth={1.5} />
                  </div>
                  <span className="font-outfit font-bold text-base text-foreground">EZ Score</span>
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${tier.bg} ${tier.text}`}>
                    {s.tier}
                  </span>
                </div>
                {s.auto_assignable
                  ? <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full"><CheckCircle className="h-3 w-3" /> Auto-Assignable</span>
                  : <span className="text-xs text-muted-foreground bg-muted px-2.5 py-1 rounded-full">Manual Review Required</span>
                }
              </div>

              {/* Score trio */}
              <div className="grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'Overall', value: s.final_score },
                  { label: 'Rule',    value: s.rule_score  },
                  { label: 'AI',      value: s.ai_score    },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-card border border-border rounded-xl p-3">
                    <p className={`text-2xl font-bold tabular-nums ${scoreColor(value)}`}>{value.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{label}</p>
                  </div>
                ))}
              </div>

              {/* Load stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="Total Loads"    value={s.stats.total_loads}     icon={Package}    />
                <StatCard label="Completed"      value={s.stats.completed_loads} icon={CheckCircle} color="text-emerald-500" />
                <StatCard label="Cancelled"      value={s.stats.cancelled_loads} icon={PowerOff}   color={s.stats.cancelled_loads > 0 ? 'text-red-500' : 'text-foreground'} />
                <StatCard label="Active Signals" value={s.stats.active_signals}  icon={Activity}   color={s.stats.active_signals > 0 ? 'text-amber-500' : 'text-foreground'} />
              </div>

              {/* Breakdown bars */}
              <div className="space-y-3 pt-1 border-t border-accent/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">Score Breakdown</p>
                {Object.entries(s.breakdown).map(([key, val]) => (
                  <ScoreBar key={key} label={key} value={val} />
                ))}
              </div>

              {/* Contributions */}
              <div className="pt-1 border-t border-accent/20">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Score Contributions</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {Object.entries(s.contributions).map(([key, val]) => (
                    <div key={key} className="bg-card border border-border rounded-lg px-3 py-2">
                      <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className={`text-sm font-bold tabular-nums ${val < 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                        {val >= 0 ? '+' : ''}{val.toFixed(1)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          );
        })()}

        {/* ── FMCSA Validation ── */}
        {(() => {
          const latest = fmcsaChecks?.[0];
          const pass   = latest?.overall_pass;
          const borderColor = pass === true ? 'border-emerald-500/40' : pass === false ? 'border-red-500/40' : 'border-border';
          const bgColor     = pass === true ? 'bg-emerald-500/[0.03]'  : pass === false ? 'bg-red-500/[0.03]'   : '';

          const FlagBadge = ({ active, label }) => active
            ? <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-600 dark:text-red-400"><AlertTriangle className="h-3 w-3" />{label}</span>
            : <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-3 w-3" />{label}</span>;

          return (
            <div className={`rounded-2xl border-2 ${borderColor} ${bgColor} p-5 space-y-4 shadow-sm`}>
              {/* Header */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2.5">
                  <div className={`p-1.5 rounded-lg ${pass === true ? 'bg-emerald-500/10' : pass === false ? 'bg-red-500/10' : 'bg-muted'}`}>
                    {pass === true
                      ? <ShieldCheck className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
                      : pass === false
                        ? <ShieldAlert className="h-4 w-4 text-red-500" strokeWidth={1.5} />
                        : <Shield className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    }
                  </div>
                  <span className="font-outfit font-bold text-base text-foreground">FMCSA Validation</span>
                  {latest && (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      pass === true  ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' :
                      pass === false ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {pass === true ? 'PASSED' : pass === false ? 'FLAGS FOUND' : 'UNKNOWN'}
                    </span>
                  )}
                </div>
                <button
                  onClick={handleRunFmcsaCheck}
                  disabled={fmcsaLoading}
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${fmcsaLoading ? 'animate-spin' : ''}`} />
                  {fmcsaLoading ? 'Checking…' : 'Re-check FMCSA'}
                </button>
              </div>

              {fmcsaLoading && !fmcsaChecks && (
                <div className="flex items-center justify-center py-6 text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Running FMCSA check…
                </div>
              )}

              {!latest && !fmcsaLoading && (
                <div className="text-center py-5 text-sm text-muted-foreground">
                  No FMCSA check on file. Click <span className="font-medium text-foreground">Re-check FMCSA</span> to run one.
                </div>
              )}

              {latest && (
                <>
                  {/* Key fields */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-card border border-border rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Authority</p>
                      <p className={`text-sm font-bold ${latest.authority_status === 'ACTIVE' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {latest.authority_status || '—'}
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Safety Rating</p>
                      <p className={`text-sm font-bold ${
                        latest.safety_rating === 'Satisfactory' ? 'text-emerald-500' :
                        latest.safety_rating === 'Conditional'  ? 'text-amber-500'   :
                        latest.safety_rating === 'Unsatisfactory' ? 'text-red-500'   : 'text-muted-foreground'
                      }`}>
                        {latest.safety_rating || 'Not Rated'}
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Insurance</p>
                      <p className={`text-sm font-bold ${latest.insurance_on_file ? 'text-emerald-500' : 'text-red-500'}`}>
                        {latest.insurance_on_file == null ? '—' : latest.insurance_on_file ? 'On File' : 'Not on File'}
                      </p>
                    </div>
                    <div className="bg-card border border-border rounded-xl p-3 text-center">
                      <p className="text-xs text-muted-foreground mb-1">Out of Service</p>
                      <p className={`text-sm font-bold ${latest.out_of_service ? 'text-red-500' : 'text-emerald-500'}`}>
                        {latest.out_of_service == null ? '—' : latest.out_of_service ? 'Yes' : 'No'}
                      </p>
                    </div>
                  </div>

                  {/* Validation flags */}
                  <div className="flex flex-wrap gap-2 pt-1 border-t border-border/60">
                    {latest.flag_no_authority != null && <FlagBadge active={latest.flag_no_authority} label="No Active Authority" />}
                    {latest.flag_safety_risk  != null && <FlagBadge active={latest.flag_safety_risk}  label="Safety Risk" />}
                    {latest.flag_no_insurance != null && <FlagBadge active={latest.flag_no_insurance}  label="No Insurance" />}
                    {latest.flag_oos          != null && <FlagBadge active={latest.flag_oos}           label="Out of Service" />}
                  </div>

                  {/* Carrier info row */}
                  {(latest.legal_name || latest.carrier_operation || latest.driver_count != null) && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1 pt-1 border-t border-border/60 text-sm">
                      {latest.legal_name && (
                        <div className="flex justify-between py-1 col-span-2 md:col-span-1">
                          <span className="text-xs text-muted-foreground">Legal Name</span>
                          <span className="text-xs font-medium text-foreground truncate ml-2">{latest.legal_name}</span>
                        </div>
                      )}
                      {latest.carrier_operation && (
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-muted-foreground">Operation</span>
                          <span className="text-xs font-medium text-foreground">{latest.carrier_operation}</span>
                        </div>
                      )}
                      {latest.driver_count != null && (
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-muted-foreground">Drivers</span>
                          <span className="text-xs font-medium text-foreground">{latest.driver_count}</span>
                        </div>
                      )}
                      {latest.power_unit_count != null && (
                        <div className="flex justify-between py-1">
                          <span className="text-xs text-muted-foreground">Power Units</span>
                          <span className="text-xs font-medium text-foreground">{latest.power_unit_count}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Footer: timestamp + history count */}
                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1 border-t border-border/60">
                    <span>
                      Checked {new Date(latest.checked_at).toLocaleString()} · Source: {latest.check_source}
                      {latest.dot_number && ` · DOT# ${latest.dot_number}`}
                    </span>
                    {fmcsaChecks.length > 1 && (
                      <span>{fmcsaChecks.length} checks on record</span>
                    )}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* ── Central Dispatch Rating ── */}
        <div className="rounded-2xl border-2 border-blue-500/40 bg-blue-500/[0.03] p-5 space-y-5 shadow-sm">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-blue-500/10">
                <Star className="h-4 w-4 text-blue-500" strokeWidth={1.5} />
              </div>
              <span className="font-outfit font-bold text-base text-foreground">Central Dispatch Rating</span>
              {cdRatings && (
                <a
                  href={`https://app.centraldispatch.com/ratings/ratings?customerid=${cdRatings.customerId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-400 transition-colors"
                >
                  <ExternalLink className="h-3 w-3" /> View on CD
                </a>
              )}
            </div>
            <button
              onClick={fetchCdRatings}
              disabled={cdLoading}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground bg-muted hover:bg-muted/80 px-3 py-1.5 rounded-full transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-3 w-3 ${cdLoading ? 'animate-spin' : ''}`} />
              {cdLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {cdLoading && !cdRatings && (
            <div className="flex items-center justify-center py-8 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading CD ratings…
            </div>
          )}

          {!cdLoading && !cdRatings && (
            <div className="text-center py-6 text-sm text-muted-foreground">
              No CD ratings available. Ensure the carrier has a Central Dispatch ID.
            </div>
          )}

          {cdRatings && (() => {
            const avg = parseFloat(cdRatings.averageRating) || 0;
            const total = cdRatings.totalReviews || 0;
            const star5 = cdRatings.total5Count || 0;
            const star4 = cdRatings.total4Count || 0;
            const star1 = cdRatings.total1Count || 0;
            const starColor = avg >= 4.5 ? 'text-emerald-500' : avg >= 3.5 ? 'text-amber-500' : 'text-red-500';

            return (
              <>
                {/* Average + totals */}
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className={`text-5xl font-bold tabular-nums ${starColor}`}>{avg.toFixed(1)}</p>
                    <div className="flex items-center justify-center gap-0.5 mt-1">
                      {[1,2,3,4,5].map(i => (
                        <Star key={i} className={`h-4 w-4 ${i <= Math.round(avg) ? 'text-amber-400 fill-amber-400' : 'text-muted'}`} />
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{total.toLocaleString()} reviews</p>
                  </div>
                  {/* Star distribution bars */}
                  <div className="flex-1 space-y-1.5">
                    {[
                      { label: '5★', count: star5 },
                      { label: '4★', count: star4 },
                      { label: '1★', count: star1 },
                    ].map(({ label, count }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-5 shrink-0">{label}</span>
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full bg-amber-400 transition-all"
                            style={{ width: total > 0 ? `${(count / total) * 100}%` : '0%' }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground tabular-nums w-8 text-right">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Category ratings */}
                {cdRatings.ratings?.length > 0 && (
                  <div className="border-t border-blue-500/20 pt-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Category Ratings</p>
                    <div className="grid grid-cols-3 gap-3 text-center">
                      {cdRatings.ratings.map(r => (
                        <div key={r.ratingType} className="bg-card border border-border rounded-xl p-3">
                          <p className="text-xl font-bold tabular-nums text-blue-500">{parseFloat(r.ratingValue).toFixed(1)}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{r.ratingType}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Optional questions */}
                {cdRatings.optionalQuestions?.length > 0 && (
                  <div className="border-t border-blue-500/20 pt-4">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Customer Feedback</p>
                    <div className="space-y-2">
                      {[...cdRatings.optionalQuestions]
                        .sort((a, b) => a.questionNumber - b.questionNumber)
                        .map(q => {
                          const pct = q.percentage === 'N/A' ? null : parseInt(q.percentage, 10);
                          return (
                            <div key={q.questionNumber} className="flex items-center justify-between gap-3">
                              <span className="text-xs text-muted-foreground">{q.answerDescription}</span>
                              {pct !== null ? (
                                <div className="flex items-center gap-2 shrink-0">
                                  <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
                                    <div className="h-full rounded-full bg-blue-500 transition-all" style={{ width: `${pct}%` }} />
                                  </div>
                                  <span className={`text-xs font-semibold tabular-nums ${pct >= 90 ? 'text-emerald-500' : pct >= 70 ? 'text-amber-500' : 'text-red-500'}`}>{pct}%</span>
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground shrink-0">N/A</span>
                              )}
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                )}

                {/* Last synced */}
                {carrier.cd_ratings_synced_at && (
                  <p className="text-xs text-muted-foreground text-right border-t border-blue-500/20 pt-3">
                    Last synced {new Date(carrier.cd_ratings_synced_at).toLocaleString()}
                  </p>
                )}
              </>
            );
          })()}
        </div>

        {/* ── FMCSA ── */}
        {ezData?.fmcsa && Object.keys(ezData.fmcsa).length > 0 && (
          <SectionCard title="FMCSA Data" icon={Shield}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
              {Object.entries(ezData.fmcsa).map(([k, v]) => (
                <InfoRow key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} value={String(v)} />
              ))}
            </div>
          </SectionCard>
        )}
        {/* Main content grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

   {/* Phone Numbers */}
          {carrier.phone_numbers?.length > 0 && (
            <SectionCard title="Phone Numbers" icon={Phone}>
              <div className="space-y-0">
                {carrier.phone_numbers.map(p => (
                  <div key={p.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{p.type}</span>
                      <p className="text-sm font-medium text-foreground">
                        {p.phone_number}{p.extension ? ` ext. ${p.extension}` : ''}
                      </p>
                      {p.notes && <p className="text-xs text-muted-foreground">{p.notes}</p>}
                    </div>
                    <a
                      href={`tel:${p.phone_number}`}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                    >
                      <Phone className="h-3.5 w-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
   

          {/* Addresses */}
          {carrier.addresses?.length > 0 && (
            <SectionCard title="Addresses" icon={MapPin}>
              <div className="space-y-3">
                {carrier.addresses.map(a => (
                  <div key={a.id} className="py-2 border-b border-border last:border-0">
                    <span className="text-xs text-muted-foreground uppercase tracking-wide">{a.type}</span>
                    <p className="text-sm font-medium text-foreground mt-0.5">{a.street_address}</p>
                    {a.street_address2 && <p className="text-sm text-muted-foreground">{a.street_address2}</p>}
                    <p className="text-sm text-muted-foreground">{[a.city, a.state, a.zipcode].filter(Boolean).join(', ')}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}
       
          {/* Authority Numbers */}
          {carrier.authority_numbers?.length > 0 && (
            <SectionCard title="Authority Numbers" icon={Award}>
              <div className="space-y-0">
                {carrier.authority_numbers.map(auth => (
                  <div key={auth.id} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wide">{auth.type?.replace(/_/g, ' ')}</span>
                      <p className="font-mono text-sm font-medium text-foreground">{auth.number}</p>
                    </div>
                    {auth.verified ? (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                        <CheckCircle className="h-3 w-3" /> Verified
                      </span>
                    ) : (
                      <span className="text-[11px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full">Unverified</span>
                    )}
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

       
          {/* Equipment & Preferences */}
          <SectionCard title="Equipment & Preferences" icon={Package}>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-muted-foreground mb-2">Equipment Types</p>
                <div className="flex flex-wrap gap-2">
                  {carrier.equipment_types?.length > 0
                    ? carrier.equipment_types.map(t => (
                      <span key={t} className="px-3 py-1 rounded-lg bg-muted text-sm font-medium text-foreground capitalize">{t}</span>
                    ))
                    : <span className="text-sm text-muted-foreground">—</span>
                  }
                  {carrier.soft_straps_supported && (
                    <span className="px-3 py-1 rounded-lg bg-emerald-500/10 text-sm font-medium text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                      <Zap className="h-3.5 w-3.5" /> EV-safe
                    </span>
                  )}
                </div>
              </div>
              <InfoRow label="Insurance Limit" value={`$${(carrier.insurance_limit || 0).toLocaleString()}`} />
              {carrier.insurance_expiry && (
                <InfoRow label="Insurance Expiry" value={new Date(carrier.insurance_expiry).toLocaleDateString()} />
              )}
              {carrier.regions_served?.length > 0 && (
                <InfoRow label="Regions Served" value={carrier.regions_served.join(', ')} />
              )}
              <InfoRow label="Communication" value={commRating !== '—' ? `${commRating} / 5` : null} />
              <InfoRow label="Total Reviews" value={carrier.total_reviews ?? 0} />
            </div>
          </SectionCard>

        </div>

        {/* System IDs */}
        <SectionCard title="System Identifiers" icon={Building2}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-0">
            <InfoRow label="Internal ID" value={carrier.id} mono />
            {carrier.customer_id && (
              <InfoRow label="Central Dispatch ID" value={carrier.customer_id} mono />
            )}
            {carrier.manheim_customer_id && (
              <InfoRow label="Manheim Customer ID" value={carrier.manheim_customer_id} mono />
            )}
            {carrier.created_at && (
              <InfoRow label="Member Since" value={new Date(carrier.created_at).toLocaleDateString()} />
            )}
          </div>
        </SectionCard>


        {/* Rating detail */}
        <SectionCard title="Performance Rating" icon={Star}>
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">Overall Internal Rating</span>
                <span className="text-sm font-bold text-foreground">{carrier.rating_internal ?? 50}/100</span>
              </div>
              <RatingBar value={carrier.rating_internal ?? 50} />
            </div>
            <div className="grid grid-cols-3 gap-4 pt-2 text-center border-t border-border">
              <div>
                <p className="text-lg font-bold text-foreground tabular-nums">{carrier.total_deliveries ?? 0}</p>
                <p className="text-xs text-muted-foreground">Deliveries</p>
              </div>
              <div>
                <p className="text-lg font-bold text-emerald-500 tabular-nums">{onTimeRate}</p>
                <p className="text-xs text-muted-foreground">On-Time</p>
              </div>
              <div>
                <p className={`text-lg font-bold tabular-nums ${carrier.damage_rate > 0.05 ? 'text-red-500' : 'text-foreground'}`}>{damageRate}</p>
                <p className="text-xs text-muted-foreground">Damage Rate</p>
              </div>
            </div>
            {(cancelRate || claimRate) && (
              <div className="grid grid-cols-2 gap-4 pt-3 text-center border-t border-border">
                {cancelRate && (
                  <div>
                    <p className={`text-lg font-bold tabular-nums ${carrier.cancel_rate > 0.1 ? 'text-amber-500' : 'text-foreground'}`}>{cancelRate}</p>
                    <p className="text-xs text-muted-foreground">Cancel Rate</p>
                  </div>
                )}
                {claimRate && (
                  <div>
                    <p className={`text-lg font-bold tabular-nums ${carrier.claim_rate > 0.05 ? 'text-red-500' : 'text-foreground'}`}>{claimRate}</p>
                    <p className="text-xs text-muted-foreground">Claim Rate</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </SectionCard>



        {/* ── Load history ── */}
        {ezData?.history?.length > 0 && (
          <SectionCard title="Load History" icon={ClipboardList}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    {Object.keys(ezData.history[0]).map(col => (
                      <th key={col} className="text-left text-xs text-muted-foreground font-medium pb-2 pr-4 capitalize whitespace-nowrap">
                        {col.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ezData.history.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {Object.values(row).map((val, j) => (
                        <td key={j} className="py-2 pr-4 text-xs text-foreground whitespace-nowrap">{String(val ?? '—')}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}

        {/* ── Fraud signals ── */}
        {ezData?.fraud_signals?.length > 0 && (
          <SectionCard title="Fraud Signals" icon={AlertTriangle}>
            <div className="space-y-2">
              {ezData.fraud_signals.map((sig, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                  <AlertTriangle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" strokeWidth={1.5} />
                  <div className="flex-1 min-w-0">
                    {typeof sig === 'string'
                      ? <p className="text-sm text-red-600 dark:text-red-400">{sig}</p>
                      : Object.entries(sig).map(([k, v]) => (
                          <p key={k} className="text-xs text-muted-foreground"><span className="font-medium text-foreground capitalize">{k.replace(/_/g, ' ')}:</span> {String(v)}</p>
                        ))
                    }
                  </div>
                </div>
              ))}
            </div>
          </SectionCard>
        )}

      </div>

      {/* ── Edit Dialog ── */}
      <Dialog open={editOpen} onOpenChange={open => { if (!open) setEditOpen(false); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-lg">Edit Carrier</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-5 pt-2">
            <CarrierForm form={form} setForm={setForm} />
            <Button type="submit" className="w-full bg-accent text-white hover:bg-accent/90" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save Changes'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
